//client/src/components/plataforma/siteInstitucional/PaginasFormPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePages } from '../../../hooks/usePages';
import useAlert from '../../../hooks/useAlert'; // ✅ Importar o hook de alerta
import { Save, Loader2, FileText } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabSaveButton from '../../common/FabSaveButton';
import GerarTextoIABox from '../../ai/GerarTextoIABox';

const PaginasFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;
    const { getPage, createPage, updatePage, loading } = usePages();
    const { setAlert } = useAlert(); // ✅ Usar o hook de alerta

    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        slug: '',
        status: 'published',
        page: 'terms',
        content: ''
    });

    const fetchPageData = useCallback(async () => {
        if (isEditing) {
            try {
                const result = await getPage(id);
                const pageData = result.data;
                setFormData({
                    title: pageData.title,
                    subtitle: pageData.subtitle || '',
                    slug: pageData.slug,
                    status: pageData.status,
                    page: pageData.page,
                    content: pageData.content?.find(b => b.type === 'html')?.content || ''
                });
            } catch (err) {
                setAlert(`Erro ao carregar dados da página: ${err.message}`, 'error'); // ✅
            }
        }
    }, [id, isEditing, getPage, setAlert]);

    useEffect(() => {
        fetchPageData();
    }, [fetchPageData]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                title: formData.title,
                subtitle: formData.subtitle,
                slug: formData.slug,
                status: formData.status,
                page: formData.page,
                content: [{
                    type: 'html',
                    content: formData.content,
                    order: 1
                }]
            };

            if (isEditing) {
                await updatePage(id, payload);
            } else {
                await createPage(payload);
            }
            setAlert(`Página "${payload.title}" salva com sucesso!`, 'success'); // ✅
            navigate('/site-institucional/paginas');
        } catch (err) {
            setAlert(`Erro ao salvar página: ${err.message}`, 'error'); // ✅
        }
    };

    return (
        <div className="page-management-container">
            <form onSubmit={handleSubmit}>
                <PageHeader
                    title={isEditing ? 'Editar Página' : 'Nova Página'}
                    icon={<FileText className="icon-title" />}
                    navBack={{ to: '/site-institucional/paginas', text: 'Voltar para a Lista' }}
                />

                <div className="content-panel mt-6">
                    <GerarTextoIABox
                        endpoint="/admin/institucional/pages/gerar-ia"
                        placeholder='Descreva a página — ex: "página Sobre Nós, tom próximo e confiante, focada em pequenos negócios de serviço"'
                        successMessage="Página gerada! Revise antes de publicar."
                        onGenerated={(data) => setFormData(prev => ({
                            ...prev,
                            title: data.title,
                            subtitle: data.subtitle,
                            content: (data.content || []).map(block => block.content).join('\n\n'),
                        }))}
                    />
                    <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                        <div className="form-group">
                            <label htmlFor="title">Título*</label>
                            <input type="text" id="title" name="title" value={formData.title} onChange={handleChange} className="form-input" required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="slug">Slug (URL)*</label>
                            <input type="text" id="slug" name="slug" value={formData.slug} onChange={handleChange} className="form-input" required placeholder="ex: termos-de-uso" />
                            <p className="form-hint">Será usado na URL: seusite.com/termos-de-uso</p>
                        </div>
                        <div className="form-group md:col-span-2">
                            <label htmlFor="subtitle">Subtítulo (Opcional)</label>
                            <input type="text" id="subtitle" name="subtitle" value={formData.subtitle} onChange={handleChange} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="status">Status</label>
                            <select id="status" name="status" value={formData.status} onChange={handleChange} className="form-input">
                                <option value="published">Publicado</option>
                                <option value="draft">Rascunho</option>
                            </select>
                        </div>
                         <div className="form-group md:col-span-2">
                            <label htmlFor="content">Conteúdo da Página (HTML)</label>
                            <textarea id="content" name="content" value={formData.content} onChange={handleChange} className="form-input" rows="15" placeholder="<h2>Título</h2><p>Parágrafo de texto...</p>"></textarea>
                            <p className="form-hint">Você pode usar tags HTML básicas como &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;.</p>
                        </div>
                    </div>
                </div>

                <FabSaveButton loading={loading} label="Salvar" />
            </form>
        </div>
    );
};

export default PaginasFormPage;